import { useState } from 'react'
import SearchableSelect from '../../../components/SearchableSelect'

// inactive = a deactivated company that is still the supplier of a card: shown (marked), never offered as a candidate.
export interface PartyOption { id: string; code: string; name: string; is_supplier: boolean; inactive?: boolean }

// Supplier dropdown (customers where is_supplier = true) plus the one-click way to add to that
// list: customers.is_supplier is a new flag and nothing sets it, so without the "mark a customer
// as a supplier" affordance the dropdown would be permanently empty. Presentational -- the parent
// owns the customer list and does the actual UPDATE in onMarkSupplier.
export default function SupplierPicker({ value, onChange, parties, canWrite, onMarkSupplier, disabled }: {
  value: string
  onChange: (id: string) => void
  parties: PartyOption[]
  canWrite: boolean
  onMarkSupplier: (customerId: string) => Promise<void>
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [pick, setPick] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const label = (p: PartyOption) => `${p.code} – ${p.name}${p.inactive ? '   [INACTIVE company]' : ''}`
  const suppliers = parties.filter(p => p.is_supplier && !p.inactive)
  const others = parties.filter(p => !p.is_supplier && !p.inactive)
  // A card's supplier may have been un-flagged since; keep showing it rather than a blank box.
  const selected = parties.find(p => p.id === value)

  const mark = async () => {
    if (!pick) return
    setBusy(true); setErr(null)
    try {
      await onMarkSupplier(pick)
      onChange(pick)
      setPick(''); setOpen(false)
    } catch (e: any) {
      setErr(e?.message || 'Could not mark as supplier')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <SearchableSelect
        options={suppliers.map(p => ({ id: p.id, label: label(p) }))}
        value={value}
        onChange={onChange}
        placeholder="— Select supplier —"
        disabled={disabled}
        selectedOption={selected ? { id: selected.id, label: label(selected) } : null}
        emptyStateHint="No suppliers yet — mark a customer as a supplier below."
      />
      {canWrite && !disabled && (
        <div className="mt-1">
          <button type="button" onClick={() => setOpen(o => !o)} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
            {open ? 'Cancel' : 'Supplier not listed? Mark a customer as a supplier'}
          </button>
          {open && (
            <div className="mt-2 p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/40 space-y-2">
              <SearchableSelect
                options={others.map(p => ({ id: p.id, label: label(p) }))}
                value={pick}
                onChange={setPick}
                placeholder="— Select an address-book entry —"
              />
              <button type="button" onClick={mark} disabled={!pick || busy}
                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50">
                {busy ? 'Saving…' : 'Mark as supplier'}
              </button>
              {err && <div className="text-xs text-red-600 dark:text-red-400">{err}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
